import React, { useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, PlusCircle } from "react-feather";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { useCreateVoucherRefillMutation, useUploadPettyCashRefillAttachmentMutation } from "../../pettyCashApi";
import StatusBadge from "../../../../components/common/StatusBadge";
import ActorName from "../../../../components/common/ActorName";
import { PETTY_CASH_REFILL_STATUS_CLASS, PETTY_CASH_REFILL_STATUS_LABEL } from "../../pettyCashStatusMaps";
import RefillAttachment from "../../refills/components/RefillAttachment";
import { paths } from "../../../../routes/paths";

const money = (n) =>
  n == null ? "-" : Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDateTime = (d) => (d ? dayjs(d).format("DD/MM/YYYY HH:mm") : "-");

// Batch215 (2026-10-22): "Fund Refill" on a voucher. The cashier asks for the fund to be
// topped up for this voucher (cheque no. + cheque photo); MD/GM approve it on the refill
// page (PettyCash.ApproveRefill credits the fund), then the voucher is paid. The parent
// owns the list query because it also blocks Pay while a refill is SUBMITTED.
const VoucherRefillCard = ({ voucher, refills, canRequest, userId, onChanged }) => {
  const [createRefill, { isLoading: isCreating }] = useCreateVoucherRefillMutation();
  const [uploadAttachment, { isLoading: isUploading }] = useUploadPettyCashRefillAttachmentMutation();
  const [showForm, setShowForm] = useState(false);
  const defaultAmount = voucher.approvedAmount ?? voucher.requestedAmount;
  const [amount, setAmount] = useState("");
  const [chequeNumber, setChequeNumber] = useState("");
  const [reason, setReason] = useState("");
  const [file, setFile] = useState(null);

  if (refills.length === 0 && !canRequest) return null;

  const openForm = () => {
    setAmount(defaultAmount != null ? String(defaultAmount) : "");
    setChequeNumber("");
    setReason("");
    setFile(null);
    setShowForm(true);
  };

  // 2026-10-23: one click requests exactly the voucher's open amount - no form. Hidden once a
  // refill is waiting or approved (an approved one has already topped the fund up for it).
  const canQuickRefill = canRequest && !refills.some((r) => r.status === "SUBMITTED" || r.status === "APPROVED");
  const handleQuickRefill = async () => {
    if (!window.confirm(`Request a fund refill of KSh ${money(defaultAmount)} for ${voucher.voucherNumber}?`)) return;
    try {
      const refill = await createRefill({
        recordGuid: voucher.recordGuid,
        requestedAmount: defaultAmount,
        reason: `Refill for voucher ${voucher.voucherNumber}`,
      }).unwrap();
      toast.success(`Refill ${refill?.requestNumber || ""} requested - awaiting approval`);
    } catch (e) {
      toast.error(e?.message || "Could not request the refill");
    }
  };

  const handleSubmit = async () => {
    const requestedAmount = Number(amount);
    if (!(requestedAmount > 0)) return toast.error("Enter a valid amount");
    if (file && file.size > 2 * 1024 * 1024) return toast.error("The cheque photo must be 2MB or smaller");
    let refill;
    try {
      refill = await createRefill({
        recordGuid: voucher.recordGuid,
        requestedAmount,
        chequeNumber: chequeNumber.trim() || undefined,
        reason: reason.trim() || undefined,
      }).unwrap();
      toast.success(`Refill ${refill?.requestNumber || ""} requested`);
    } catch (e) {
      toast.error(e?.message || "Could not request the refill");
      return;
    }
    if (file && refill?.recordGuid) {
      try {
        await uploadAttachment({ recordGuid: refill.recordGuid, file }).unwrap();
        toast.success("Cheque photo attached");
        // The list may still be refetching from the create, so ask once more for hasAttachment.
        onChanged?.();
      } catch (e) {
        // The request exists either way - it can still be attached on the card below.
        toast.error(e?.message || "The refill was requested but the cheque photo could not be attached - add it below.");
      }
    }
    setShowForm(false);
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div className="vd-card-title mb-0"><RefreshCw size={16} /> Fund Refill</div>
          {canRequest && !showForm && (
            <button type="button" className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1" onClick={openForm}>
              <PlusCircle size={14} /> Refill with cheque details
            </button>
          )}
        </div>

        {canQuickRefill && !showForm && defaultAmount > 0 && (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 border rounded p-3 mb-3">
            <div>
              <div className="text-muted small">Open amount</div>
              <div className="tx-money-out" style={{ fontSize: 22, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                KSh {money(defaultAmount)}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-added d-inline-flex align-items-center gap-1"
              onClick={handleQuickRefill}
              disabled={isCreating}
            >
              <RefreshCw size={14} /> {isCreating ? "Requesting..." : "Refill Fund"}
            </button>
          </div>
        )}

        {showForm && canRequest && (
          <div className="border rounded p-3 mb-3">
            <p className="text-muted small mb-3">
              Asks for <strong>{voucher.fundName || "the fund"}</strong> to be topped up so {voucher.voucherNumber} can be
              paid. The amount is added to the fund when MD/GM approve the refill; the voucher cannot be paid until then.
            </p>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Amount</label>
                <input type="number" min="0" step="0.01" className="form-control" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Cheque Number (optional)</label>
                <input type="text" maxLength={50} className="form-control" value={chequeNumber} onChange={(e) => setChequeNumber(e.target.value)} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Cheque photo (optional)</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  className="form-control"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                <div className="form-text">JPEG, PNG or PDF, up to 2MB.</div>
              </div>
              <div className="col-md-12 mb-3">
                <label className="form-label">Reason (optional)</label>
                <input type="text" maxLength={300} className="form-control" value={reason} onChange={(e) => setReason(e.target.value)} />
              </div>
            </div>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-added" onClick={handleSubmit} disabled={isCreating || isUploading}>
                {isCreating || isUploading ? "Submitting..." : "Submit Refill Request"}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </div>
        )}

        {refills.length === 0 && !showForm && <div className="text-muted small">No refill requested for this voucher.</div>}

        {refills.map((r) => {
          // Same rule as RefillDetail: the requester attaches/replaces while it awaits approval.
          const canManageAttachment = r.status === "SUBMITTED" && userId != null && r.requestedByUserId === userId;
          return (
            <div key={r.recordGuid} className="border rounded p-3 mb-2">
              <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
                <Link to={paths.pettyCashRefillDetail(r.recordGuid)} className="fw-bold">{r.requestNumber}</Link>
                <span className="tx-money-in" style={{ fontSize: 22, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                  KSh {money(r.requestedAmount)}
                </span>
                <StatusBadge status={r.status} classMap={PETTY_CASH_REFILL_STATUS_CLASS} labelMap={PETTY_CASH_REFILL_STATUS_LABEL} />
                {r.chequeNumber && <span className="small"><span className="text-muted">Cheque No.</span> <strong>{r.chequeNumber}</strong></span>}
              </div>
              <div className="small mb-2">
                <div>
                  Requested by <ActorName action="Requested" name={r.requestedByName} />
                  <span className="text-muted ms-2">{fmtDateTime(r.requestedDate)}</span>
                </div>
                {r.approvedByName && (
                  <div>
                    Approved by <ActorName action="Approved" name={r.approvedByName} />
                    <span className="text-muted ms-2">{fmtDateTime(r.approvedDate)}</span>
                  </div>
                )}
                {r.status === "REJECTED" && r.rejectionReason && (
                  <div className="text-danger">Rejected: {r.rejectionReason}</div>
                )}
                {r.reason && <div className="text-muted">Reason: {r.reason}</div>}
              </div>
              {(r.hasAttachment || canManageAttachment) && (
                <RefillAttachment
                  recordGuid={r.recordGuid}
                  hasAttachment={r.hasAttachment}
                  fileName={r.attachmentFileName}
                  canManage={canManageAttachment}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VoucherRefillCard;
